/**
 * @fileoverview Zcash Wallet React Hook
 * Provides React integration for Zcash wallet functionality
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
	zcashWallet,
	type WalletConnection,
	type SupportedWalletType,
	type TransactionData,
	type SignedTransaction,
	type ZcashBalance,
	type WalletError
} from '../services/zcashWallet';
import type { ScanProgress } from '../services/winbit32Wallet';

export interface UseZcashWalletResult {
	// Connection state
	isConnected: boolean;
	connection: WalletConnection | null;
	isConnecting: boolean;
	connectionError: string | null;
	
	// Wallet operations
	connect: (walletType: SupportedWalletType) => Promise<void>;
	unlock: (password: string) => Promise<void>;
	disconnect: () => Promise<void>;
	
	// Balance
	balance: ZcashBalance | null;
	isLoadingBalance: boolean;
	balanceError: string | null;
	refreshBalance: () => void;
	
	// Transactions
	createTransaction: (txData: TransactionData) => Promise<SignedTransaction>;
	isCreatingTransaction: boolean;
	transactionError: string | null;
	
	// Utilities
	availableWallets: SupportedWalletType[];
	formatAddress: (address: string, length?: number) => string;
	formatAmount: (amount: number) => string;
	validateAddress: (address: string) => boolean;
	scanProgress: ScanProgress | null;
	
}

export const useZcashWallet = (): UseZcashWalletResult => {
	const queryClient = useQueryClient();
	
	// Local state
	const [isConnecting, setIsConnecting] = useState(false);
	const [connectionError, setConnectionError] = useState<string | null>(null);
	const [transactionError, setTransactionError] = useState<string | null>(null);
	const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
	
	// Wallet connection state
	const connection = zcashWallet.getConnection();
	const isConnected = zcashWallet.isConnected();
	
	// Available wallets
	const availableWallets = zcashWallet.getAvailableWallets();
	
	// Balance query
	const {
		data: balance,
		isLoading: isLoadingBalance,
		error: balanceError,
		refetch: refreshBalance
	} = useQuery({
		queryKey: ['zcash-balance'],
		queryFn: async () => {
			if (!isConnected) return null;
			return await zcashWallet.getBalance();
		},
		enabled: isConnected,
		refetchInterval: 30000, // Refresh every 30 seconds
		retry: 3,
		retryDelay: 1000
	});
	
	// Connect wallet mutation
	const connectMutation = useMutation({
		mutationFn: async (walletType: SupportedWalletType) => {
			return await zcashWallet.connect(walletType);
		},
		onSuccess: (_connection) => {
			setConnectionError(null);
			// Invalidate and refetch balance
			queryClient.invalidateQueries({ queryKey: ['zcash-balance'] });
		},
		onError: (error: WalletError) => {
			setConnectionError(error.message);
		}
	});
	
	// Transaction creation mutation
	const transactionMutation = useMutation({
		mutationFn: async (txData: TransactionData) => {
			return await zcashWallet.createShieldedTransaction(txData);
		},
		onSuccess: () => {
			setTransactionError(null);
			// Refresh balance after transaction
			queryClient.invalidateQueries({ queryKey: ['zcash-balance'] });
		},
		onError: (error: any) => {
			// Check if this is a manual transaction error with details
			if (error.message === 'MANUAL_TRANSACTION_REQUIRED' && error.transactionDetails) {
				// Re-throw the error with transaction details for the UI to handle
				throw error;
			}
			setTransactionError(error.message);
		}
	});
	
	// Unlock (decrypt) an encrypted Winbit32 seed and connect.
	const unlockMutation = useMutation({
		mutationFn: async (password: string) => {
			return await zcashWallet.unlockWinbit32(password);
		},
		onSuccess: () => {
			setConnectionError(null);
			queryClient.invalidateQueries({ queryKey: ['zcash-balance'] });
		},
		onError: (error: WalletError) => {
			setConnectionError(error.message);
		}
	});

	// Connect function
	const connect = useCallback(async (walletType: SupportedWalletType) => {
		setIsConnecting(true);
		setConnectionError(null);
		
		try {
			await connectMutation.mutateAsync(walletType);
		} catch (error) {
			// Error is also handled by mutation onError, but callers may need to react.
			throw error;
		} finally {
			setIsConnecting(false);
		}
	}, [connectMutation]);

	// Unlock function
	const unlock = useCallback(async (password: string) => {
		setIsConnecting(true);
		setConnectionError(null);
		try {
			await unlockMutation.mutateAsync(password);
		} catch (error) {
			throw error;
		} finally {
			setIsConnecting(false);
		}
	}, [unlockMutation]);
	
	// Disconnect function
	const disconnect = useCallback(async () => {
		try {
			await zcashWallet.disconnect();
			queryClient.removeQueries({ queryKey: ['zcash-balance'] });
			setConnectionError(null);
			setTransactionError(null);
		} catch (error) {
			console.warn('Disconnect error:', error);
		}
	}, [queryClient]);
	
	// Create transaction function
	const createTransaction = useCallback(async (txData: TransactionData): Promise<SignedTransaction> => {
		setTransactionError(null);
		return await transactionMutation.mutateAsync(txData);
	}, [transactionMutation]);
	
	// Utility functions
	const formatAddress = useCallback((address: string, length: number = 8): string => {
		if (address.length <= length * 2) return address;
		return `${address.slice(0, length)}...${address.slice(-length)}`;
	}, []);
	
	const formatAmount = useCallback((amount: number): string => {
		return `${amount.toFixed(6)} ZEC`;
	}, []);
	
	const validateAddress = useCallback((address: string): boolean => {
		// Shielded addresses (Sapling and Orchard)
		if (address.startsWith('zs1') || address.startsWith('u1')) {
			return address.length >= 78;
		}
		
		// Transparent addresses
		if (address.startsWith('t1') || address.startsWith('t3')) {
			return address.length >= 34 && address.length <= 35;
		}
		
		return false;
	}, []);
	
	useEffect(() => {
		setScanProgress(zcashWallet.getScanProgress());
		const unsubscribe = zcashWallet.onScanProgress((progress) => {
			setScanProgress(progress);
		});
		return unsubscribe;
	}, []);
	
	// Auto-refresh balance when connection changes
	useEffect(() => {
		if (isConnected) {
			queryClient.invalidateQueries({ queryKey: ['zcash-balance'] });
		}
	}, [isConnected, queryClient]);
	
	return {
		// Connection state
		isConnected,
		connection,
		isConnecting: isConnecting || connectMutation.isPending,
		connectionError,
		
		// Wallet operations
		connect,
		unlock,
		disconnect,
		
		// Balance
		balance: balance || null,
		isLoadingBalance,
		balanceError: balanceError?.message || null,
		refreshBalance,
		
		// Transactions
		createTransaction,
		isCreatingTransaction: transactionMutation.isPending,
		transactionError,
		
		// Utilities
		availableWallets,
		formatAddress,
		formatAmount,
		validateAddress,
		scanProgress
	};
};

// Hook for specific wallet operations
export const useZcashTransaction = () => {
	const { createTransaction, isCreatingTransaction, transactionError } = useZcashWallet();
	
	const sendNFPTTransaction = useCallback(async (
		recipient: string,
		memo: string,
		amount: number = 0.0001, // Minimal amount for memo
		options?: {
			allowTransparentInputs?: boolean
			skipShieldedScan?: boolean
			abortSignal?: AbortSignal
		}
	): Promise<SignedTransaction> => {
		try {
			return await createTransaction({
				recipient,
				amount,
				memo,
				fee: 0.0001,
				allowTransparentInputs: options?.allowTransparentInputs === true,
				skipShieldedScan: options?.skipShieldedScan === true,
				abortSignal: options?.abortSignal
			});
		} catch (error: any) {
			// Check if this is a manual transaction error
			if (error.message === 'MANUAL_TRANSACTION_REQUIRED' && error.transactionDetails) {
				// For manual transactions, we need the UI to handle this
				// Re-throw with additional context
				const manualError = new Error('MANUAL_TRANSACTION_REQUIRED') as any;
				manualError.transactionDetails = {
					...error.transactionDetails,
					description: 'Complete this NFPT transaction manually in your Zcash wallet'
				};
				throw manualError;
			}
			throw error;
		}
	}, [createTransaction]);
	
	const sendPaymentTransaction = useCallback(async (
		recipient: string,
		amount: number,
		memo?: string,
		options?: {
			allowTransparentInputs?: boolean
			skipShieldedScan?: boolean
			abortSignal?: AbortSignal
		}
	): Promise<SignedTransaction> => {
		try {
			return await createTransaction({
				recipient,
				amount,
				memo,
				fee: 0.0001,
				allowTransparentInputs: options?.allowTransparentInputs === true,
				skipShieldedScan: options?.skipShieldedScan === true,
				abortSignal: options?.abortSignal
			});
		} catch (error: any) {
			// Check if this is a manual transaction error
			if (error.message === 'MANUAL_TRANSACTION_REQUIRED' && error.transactionDetails) {
				// For manual transactions, we need the UI to handle this
				// Re-throw with additional context
				const manualError = new Error('MANUAL_TRANSACTION_REQUIRED') as any;
				manualError.transactionDetails = {
					...error.transactionDetails,
					description: 'Complete this payment transaction manually in your Zcash wallet'
				};
				throw manualError;
			}
			throw error;
		}
	}, [createTransaction]);
	
	return {
		sendNFPTTransaction,
		sendPaymentTransaction,
		isCreatingTransaction,
		transactionError
	};
};

// Hook for wallet balance management
export const useZcashBalance = () => {
	const { balance, isLoadingBalance, balanceError, refreshBalance, isConnected } = useZcashWallet();
	
	const hasEnoughBalance = useCallback((requiredAmount: number): boolean => {
		if (!balance) return false;
		return balance.total >= requiredAmount;
	}, [balance]);
	
	const getShieldedBalance = useCallback((): number => {
		return balance?.shielded || 0;
	}, [balance]);
	
	const getTransparentBalance = useCallback((): number => {
		return balance?.transparent || 0;
	}, [balance]);
	
	const getTotalBalance = useCallback((): number => {
		return balance?.total || 0;
	}, [balance]);
	
	return {
		balance,
		isLoadingBalance,
		balanceError,
		refreshBalance,
		isConnected,
		hasEnoughBalance,
		getShieldedBalance,
		getTransparentBalance,
		getTotalBalance
	};
};

// Hook for wallet validation utilities
export const useZcashValidation = () => {
	const { validateAddress, formatAddress, formatAmount } = useZcashWallet();
	
	const validateMemo = useCallback((memo: string): { isValid: boolean; error?: string } => {
		if (!memo) return { isValid: true };
		
		const byteLength = new TextEncoder().encode(memo).length;
		if (byteLength > 512) {
			return {
				isValid: false,
				error: `Memo too long: ${byteLength} bytes (max 512 bytes)`
			};
		}
		
		return { isValid: true };
	}, []);
	
	const validateTransactionData = useCallback((txData: Partial<TransactionData>): {
		isValid: boolean;
		errors: string[];
	} => {
		const errors: string[] = [];
		
		if (!txData.recipient) {
			errors.push('Recipient address is required');
		} else if (!validateAddress(txData.recipient)) {
			errors.push('Invalid recipient address format');
		}
		
		if (txData.amount !== undefined && txData.amount <= 0) {
			errors.push('Amount must be greater than zero');
		}
		
		if (txData.memo) {
			const memoValidation = validateMemo(txData.memo);
			if (!memoValidation.isValid) {
				errors.push(memoValidation.error!);
			}
		}
		
		return {
			isValid: errors.length === 0,
			errors
		};
	}, [validateAddress, validateMemo]);
	
	return {
		validateAddress,
		validateMemo,
		validateTransactionData,
		formatAddress,
		formatAmount
	};
};